import { useState } from "react";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import {
  buildContentProps,
  buildDialogProps,
  buildFileHandlers,
  buildFolderTreeProps,
  buildToolbarProps,
} from "./documentLibraryProps";
import { useFileData } from "./useFileData";
import { useFileMutations } from "./useFileMutations";
import { useFolderData } from "./useFolderData";
import { useFolderMutations } from "./useFolderMutations";

/**
 * Owns all state, data fetching and mutations for the document library page, and returns the
 * four prop groups its layout components need: toolbar, folderTree, content and dialog.
 */
export const useDocumentLibrary = (universityId, navigate) => {
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [query, setQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [folderDialog, setFolderDialog] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [sendFile, setSendFile] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);

  const canCreate = usePermission(P.UNIVERSITY_DOCUMENT.CREATE);
  const canEdit = usePermission(P.UNIVERSITY_DOCUMENT.UPDATE);
  const canDelete = usePermission(P.UNIVERSITY_DOCUMENT.DELETE);
  const canSend = usePermission(P.UNIVERSITY_DOCUMENT.SEND);

  const { folders, breadcrumb } = useFolderData(universityId, selectedFolderId);
  const { filesQuery, searchQuery } = useFileData(universityId, selectedFolderId, activeSearch);
  const { folderMutation, deleteFolderMutation } = useFolderMutations(universityId, {
    onSaved: () => setFolderDialog(null),
    onDeleted: () => setSelectedFolderId(null),
  });
  const { uploadMutation, fileMutation, deleteFileMutation } = useFileMutations(
    universityId,
    selectedFolderId,
    uploadName,
    () => setUploadName("")
  );

  const clearSearch = () => {
    setQuery("");
    setActiveSearch("");
  };
  const openFromSearch = (folderId) => {
    setSelectedFolderId(folderId);
    clearSearch();
  };

  const fileHandlers = buildFileHandlers({
    navigate,
    universityId,
    canEdit,
    canDelete,
    canSend,
    fileMutation,
    deleteFileMutation,
    onSend: setSendFile,
    onConfirmDelete: setDeleteRequest,
  });

  const toolbar = buildToolbarProps({
    canCreate,
    setFolderDialog,
    query,
    setQuery,
    setActiveSearch,
    clearSearch,
    activeSearch,
  });
  const folderTree = buildFolderTreeProps({
    folders,
    selectedFolderId,
    setSelectedFolderId,
    setFolderDialog,
    deleteFolderMutation,
    onConfirmDelete: setDeleteRequest,
    canCreate,
    canEdit,
    canDelete,
  });
  const content = buildContentProps({
    activeSearch,
    searchQuery,
    openFromSearch,
    breadcrumb,
    selectedFolderId,
    setSelectedFolderId,
    canCreate,
    uploadName,
    setUploadName,
    uploadMutation,
    filesQuery,
    fileHandlers,
  });
  const dialog = buildDialogProps({ folderDialog, setFolderDialog, folderMutation });
  const sendDialog = {
    open: Boolean(sendFile),
    universityId,
    folderId: selectedFolderId,
    preselectedFileId: sendFile?.id,
    onClose: () => setSendFile(null),
  };

  const confirmDelete = {
    request: deleteRequest,
    onCancel: () => setDeleteRequest(null),
    onConfirm: () => {
      deleteRequest?.run();
      setDeleteRequest(null);
    },
  };

  return { toolbar, folderTree, content, dialog, sendDialog, confirmDelete };
};
